import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { StickyContainerContext } from '../StickyContainer/StickyContainer.js';

/**
 * The Sticky Element will show when utilizes the context from the StickyWrapper and will display based on either that or the isSticky prop.
 * The scrollDirection will control whether the component will display on scroll down, up, or both while the isSticky context is true.
 **/
import { jsx as _jsx } from "react/jsx-runtime";
const ComponentWithContext = Component => props => /*#__PURE__*/_jsx(StickyContainerContext.Consumer, {
  children: _ref => {
    let {
      isScrollingUp,
      isSticky
    } = _ref;
    return /*#__PURE__*/_jsx(Component, {
      ...props,
      isSticky: isSticky,
      isScrollingUp: isScrollingUp
    });
  }
});
function StickyElement(props) {
  const size = useRef(props.initialSize ?? 0);
  let cmp;
  useEffect(() => {
    if (document) {
      const elementRect = cmp.getBoundingClientRect();
      const isFixedToSide = props.position === 'left' || props.position === 'right';
      size.current = isFixedToSide ? elementRect.width : elementRect.height;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isScrollDirectionMatching = () => {
    const {
      scrollDirection = 'both',
      isScrollingUp
    } = props;
    if (scrollDirection === 'up' && isScrollingUp) {
      return true;
    }
    if (scrollDirection === 'down' && !isScrollingUp) {
      return true;
    }
    if (scrollDirection === 'both') {
      return true;
    }
    return false;
  };
  const {
    children,
    position = 'top',
    isAnimated = true,
    remainSticky,
    className,
    fullHeight,
    instance = 'one',
    isScrollingUp,
    offset,
    offsetTop = 0,
    hasBoxShadow = true,
    scrollDetection = false,
    ...rest
  } = props;
  delete rest.initialSize;
  delete rest.isSticky;
  delete rest.scrollDirection;
  const showSticky = props.isSticky && props.isSticky[instance] && isScrollDirectionMatching();
  const classNames = clsx(className, position, `offset-top-${offsetTop}`, {
    show: remainSticky || showSticky,
    animated: isAnimated,
    'scroll-detection': scrollDetection,
    'scroll-up': isScrollingUp && scrollDetection,
    'has-shadow': hasBoxShadow,
    'full-height': fullHeight
  });
  const getTransformStyle = () => {
    switch (position) {
      case 'top':
        return {
          transform: `translateY(${-size.current}px)`
        };
      case 'bottom':
        return {
          transform: `translateY(${size.current}px)`
        };
      case 'left':
        return {
          transform: `translateX(${-size.current}px)`
        };
      case 'right':
        return {
          transform: `translateX(${size.current}px)`
        };
      default:
        return {};
    }
  };
  const getOffsetStyle = () => {
    if (typeof offset === 'number') {
      return {
        [position]: `${offset}px`
      };
    }
    try {
      return Object.entries(offset).reduce((acc, _ref2) => {
        let [currentPosition, currentValue] = _ref2;
        acc[currentPosition] = `${currentValue}px`;
        return acc;
      }, {});
    } catch (e) {
      return {};
    }
  };
  const styles = {
    ...getTransformStyle(),
    ...getOffsetStyle()
  };
  return /*#__PURE__*/_jsx("div", {
    "data-cmp": "stickyElement",
    ref: c => {
      cmp = c;
    },
    className: `sticky-container ${classNames}`,
    style: styles,
    ...rest,
    children: children
  });
}
export default ComponentWithContext(StickyElement);